// src/screens/AyarlarScreen.js
import React, { useContext, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView,
  TouchableOpacity, Switch, Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AuthContext } from '../context/AuthContext';
import { NotificationsContext } from '../context/NotificationsContext';
import CONFIG from '../config/Config';

// ── Yardımcı bileşenler ───────────────────────────────────────

const SectionLabel = ({ title }) => (
  <Text style={styles.sectionLabel}>{title}</Text>
);

const SettingsRow = ({
  icon, iconBg, title, subtitle,
  onPress, rightElement, danger = false
}) => (
  <TouchableOpacity
    style={styles.row}
    onPress={onPress}
    activeOpacity={onPress ? 0.6 : 1}
    disabled={!onPress && !rightElement?.type?.name === 'Switch'}
  >
    <View style={[styles.rowIcon, { backgroundColor: iconBg }]}>
      <Ionicons name={icon} size={18} color={danger ? '#DC2626' : '#555'} />
    </View>
    <View style={styles.rowBody}>
      <Text style={[styles.rowTitle, danger && { color: '#DC2626' }]}>{title}</Text>
      {subtitle ? <Text style={styles.rowSub}>{subtitle}</Text> : null}
    </View>
    {rightElement ? rightElement : onPress ? (
      <Ionicons name="chevron-forward" size={16} color="#C0C0C0" />
    ) : null}
  </TouchableOpacity>
);

const Badge = ({ label, type = 'default' }) => {
  const colors = {
    default: { bg: '#FEF3C7', text: '#92400E' },
    success: { bg: '#D1FAE5', text: '#065F46' },
    danger:  { bg: '#FEE2E2', text: '#991B1B' },
  };
  const c = colors[type] || colors.default;
  return (
    <View style={[styles.badge, { backgroundColor: c.bg }]}>
      <Text style={[styles.badgeText, { color: c.text }]}>{label}</Text>
    </View>
  );
};

// ── Ana Ekran ─────────────────────────────────────────────────

export default function AyarlarScreen({ navigation }) {
  const { logout, userToken } = useContext(AuthContext);
  const {
    expoPushToken,
    permissionStatus,
    syncState,
    syncError,
    registrationError,
    pushEnabled,
    soundEnabled,
    setPushEnabled,
    setSoundEnabled,
    requestPermissions,
    retrySync,
  } = useContext(NotificationsContext);
  const userInfo  = userToken ? JSON.parse(userToken) : null;
  const userLevel = userInfo?.Level || 0;

  const [otomatikFis, setOtomatikFis] = useState(false);

  const initials = userInfo?.Name
    ? userInfo.Name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'KU';

  const levelLabel = {
    1: 'Kasiyer',
    2: 'Kıdemli Kasiyer',
    3: 'Junior Yönetici',
    4: 'Yönetici',
    5: 'Süper Admin',
  }[userLevel] || 'Kullanıcı';

  const handleLogout = () => {
    Alert.alert(
      'Çıkış Yap',
      'Oturumu kapatmak istediğinize emin misiniz?',
      [
        { text: 'İptal', style: 'cancel' },
        { text: 'Çıkış Yap', style: 'destructive', onPress: () => logout() },
      ]
    );
  };

  const handleSunucuAdresi = () => {
    Alert.alert('Sunucu Adresi', `Mevcut: ${CONFIG.API_BASE_URL}`);
  };

  const handleSifreDegistir = () => {
    // navigation.navigate('SifreDegistir')
    Alert.alert('Bilgi', 'Şifre değiştirme ekranı yakında.');
  };

  const notificationBadge = (() => {
    if (permissionStatus === 'granted') {
      if (registrationError)         return { label: 'FCM Eksik', type: 'danger' };
      if (syncState === 'synced')  return { label: 'Aktif', type: 'success' };
      if (syncState === 'error')   return { label: 'Sunucu Hatası', type: 'danger' };
      if (syncState === 'syncing') return { label: 'Kaydediliyor…', type: 'default' };
      return { label: 'Hazır', type: 'success' };
    }
    if (permissionStatus === 'expo-go-unsupported') return { label: 'Dev Build Gerekli', type: 'default' };
    if (permissionStatus === 'denied')              return { label: 'Kapalı', type: 'danger' };
    return { label: 'İzin Bekliyor', type: 'default' };
  })();

  const notificationSubtitle = (() => {
    if (permissionStatus === 'granted') {
      if (registrationError) return `FCM: ${registrationError}`;
      if (syncState === 'error' && syncError) return `Hata: ${syncError}`;
      return expoPushToken ? `Token: ${expoPushToken.slice(-10)}` : 'Cihaz kaydı hazırlanıyor';
    }
    if (permissionStatus === 'expo-go-unsupported') return 'Expo Go yerine Development Build kullanın';
    if (permissionStatus === 'device-required')     return 'Gerçek cihaz gerekli';
    return 'Uzaktan sipariş bildirimleri için izin verin';
  })();

  const handlePushToggle = async (val) => {
    if (val && permissionStatus !== 'granted') {
      // İzin henüz alınmamış → iste
      if (permissionStatus === 'denied' || permissionStatus === 'expo-go-unsupported') {
        Alert.alert(
          'Bildirim İzni Gerekli',
          permissionStatus === 'expo-go-unsupported'
            ? 'Push bildirimleri için Development Build (EAS) kullanın.'
            : 'Sistem ayarlarından bildirim iznini açmanız gerekiyor.',
        );
        return;
      }
      const token = await requestPermissions();
      if (token) {
        await setPushEnabled(true);
      }
      return;
    }
    await setPushEnabled(val);
  };

  const handlePushNotifications = async () => {
    if (permissionStatus === 'expo-go-unsupported') {
      Alert.alert(
        'Push Bildirimleri',
        'Expo Go ile uzak push bildirimleri desteklenmez. Development Build (EAS) ile açmanız gerekir.'
      );
      return;
    }

    // Token var ama sunucuya kayıt başarısız oldu → yeniden dene
    if (permissionStatus === 'granted' && syncState === 'error') {
      Alert.alert(
        'Sunucu Kayıt Hatası',
        syncError ? `Hata: ${syncError}\n\nTekrar denemek ister misiniz?` : 'Sunucuya kayıt başarısız. Tekrar denemek ister misiniz?',
        [
          { text: 'İptal', style: 'cancel' },
          { text: 'Tekrar Dene', onPress: retrySync },
        ]
      );
      return;
    }

    if (permissionStatus === 'granted' && registrationError) {
      Alert.alert(
        'FCM Yapılandırması Eksik',
        registrationError.includes('FirebaseApp is not initialized')
          ? 'Android tarafında Firebase/FCM kurulumu eksik. google-services.json dosyasını Firebase projesinden indirip android/app altına koymanız ve yeni development build almanız gerekiyor.'
          : registrationError
      );
      return;
    }

    const token = await requestPermissions();

    if (token) {
      Alert.alert('Push Bildirimleri', 'Cihaz push bildirimleri için kaydedildi.');
      return;
    }

    if (permissionStatus === 'denied') {
      Alert.alert(
        'Bildirim İzni Gerekli',
        'Push bildirim almak için sistem ayarlarından bildirim iznini açın.'
      );
      return;
    }

    Alert.alert('Push Bildirimleri', 'Bildirim kaydı tamamlanamadı.');
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>

      {/* Kullanıcı Başlığı */}
      <View style={styles.profileHeader}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{initials}</Text>
        </View>
        <View style={styles.profileInfo}>
          <Text style={styles.profileName}>{userInfo?.Name || 'Kullanıcı'}</Text>
          <Text style={styles.profileSub}>{levelLabel} · Kasap POS</Text>
        </View>
        <View style={styles.levelBadge}>
          <Text style={styles.levelBadgeText}>Level {userLevel}</Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >

        {/* Hesap */}
        <SectionLabel title="Hesap" />
        <View style={styles.card}>
          <SettingsRow
            icon="person-outline"
            iconBg="#E3F2FD"
            title="Profil Bilgileri"
            subtitle={userInfo?.Name || '—'}
            onPress={() => {}}
          />
          <SettingsRow
            icon="lock-closed-outline"
            iconBg="#E8F5E9"
            title="Şifre Değiştir"
            subtitle="Güvenlik ayarları"
            onPress={handleSifreDegistir}
          />
        </View>

        {/* Yazıcı & Fiş */}
        <SectionLabel title="Yazıcı & Fiş" />
        <View style={styles.card}>
          <SettingsRow
            icon="print-outline"
            iconBg="#FFF3E0"
            title="Fiş Yazıcısı"
            subtitle="Bağlantı ayarları"
            onPress={() => {}}
            rightElement={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Badge label="Bağlı Değil" type="danger" />
                <Ionicons name="chevron-forward" size={16} color="#C0C0C0" />
              </View>
            }
          />
          <SettingsRow
            icon="receipt-outline"
            iconBg="#FFF3E0"
            title="Fiş Başlığı"
            subtitle="İşletme adı ve bilgileri"
            onPress={() => {}}
          />
          <SettingsRow
            icon="document-text-outline"
            iconBg="#FFF3E0"
            title="Otomatik Fiş Kes"
            subtitle="Adisyon kapanınca yazdır"
            rightElement={
              <Switch
                value={otomatikFis}
                onValueChange={setOtomatikFis}
                trackColor={{ false: '#E0E0E0', true: '#4CAF50' }}
                thumbColor="#fff"
              />
            }
          />
        </View>

        {/* Uygulama */}
        <SectionLabel title="Uygulama" />
        <View style={styles.card}>
          <SettingsRow
            icon="globe-outline"
            iconBg="#EDE7F6"
            title="Sunucu Adresi"
            subtitle={CONFIG.API_BASE_URL}
            onPress={handleSunucuAdresi}
          />
          <SettingsRow
            icon="notifications-outline"
            iconBg="#FEF3C7"
            title="Push Bildirimleri"
            subtitle="Stabil mod: her zaman açık"
            onPress={permissionStatus !== 'granted' ? handlePushNotifications : undefined}
            rightElement={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {permissionStatus !== 'granted' && (
                  <Badge label={notificationBadge.label} type={notificationBadge.type} />
                )}
                <Switch
                  value={true}
                  onValueChange={handlePushToggle}
                  disabled={true}
                  trackColor={{ false: '#E0E0E0', true: '#FF9800' }}
                  thumbColor="#fff"
                />
              </View>
            }
          />
          <SettingsRow
            icon="volume-high-outline"
            iconBg="#E8F5E9"
            title="Sesli Bildirimler"
            subtitle="Stabil mod: her zaman açık"
            rightElement={
              <Switch
                value={true}
                onValueChange={setSoundEnabled}
                disabled={true}
                trackColor={{ false: '#E0E0E0', true: '#4CAF50' }}
                thumbColor="#fff"
              />
            }
          />
          <SettingsRow
            icon="time-outline"
            iconBg="#E3F2FD"
            title="Oturum Zaman Aşımı"
            subtitle="30 dakika"
            onPress={() => {}}
          />
        </View>

        {/* Hakkında */}
        <SectionLabel title="Hakkında" />
        <View style={styles.card}>
          <SettingsRow
            icon="phone-portrait-outline"
            iconBg="#F1F5F9"
            title="Uygulama Versiyonu"
            subtitle="v1.0.0"
            rightElement={<Badge label="Güncel" type="success" />}
          />
          <SettingsRow
            icon="help-circle-outline"
            iconBg="#F1F5F9"
            title="Destek"
            subtitle="Sorun bildir"
            onPress={() => {}}
          />
        </View>

        {/* Çıkış */}
        <SectionLabel title="Oturum" />
        <View style={styles.card}>
          <SettingsRow
            icon="log-out-outline"
            iconBg="#FEE2E2"
            title="Çıkış Yap"
            subtitle={`${userInfo?.Name || 'Kullanıcı'} olarak giriş yapıldı`}
            onPress={handleLogout}
            danger
          />
        </View>

        <Text style={styles.versionFooter}>Kasap POS · v1.0.0</Text>

      </ScrollView>
    </SafeAreaView>
  );
}

// ── Stiller ───────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:     { flex: 1, backgroundColor: '#F5F7FA' },

  profileHeader: { backgroundColor: '#2C3E50', flexDirection: 'row',
                   alignItems: 'center', padding: 20, gap: 12 },
  avatar:        { width: 50, height: 50, borderRadius: 25,
                   backgroundColor: '#4a6278', justifyContent: 'center', alignItems: 'center' },
  avatarText:    { color: '#fff', fontSize: 18, fontWeight: '700' },
  profileInfo:   { flex: 1 },
  profileName:   { color: '#fff', fontSize: 16, fontWeight: '700' },
  profileSub:    { color: '#94A3B8', fontSize: 12, marginTop: 2 },
  levelBadge:    { backgroundColor: '#3B82F6', paddingHorizontal: 10,
                   paddingVertical: 4, borderRadius: 20 },
  levelBadgeText:{ color: '#DBEAFE', fontSize: 12, fontWeight: '700' },

  scroll:        { padding: 16, paddingBottom: 60 },

  sectionLabel:  { fontSize: 11, fontWeight: '700', color: '#7F8C8D',
                   letterSpacing: 0.6, textTransform: 'uppercase',
                   marginBottom: 6, marginTop: 16, marginLeft: 4 },

  card:          { backgroundColor: '#fff', borderRadius: 14,
                   borderWidth: 0.5, borderColor: '#E8ECF0',
                   overflow: 'hidden', marginBottom: 4 },

  row:           { flexDirection: 'row', alignItems: 'center',
                   padding: 13, gap: 12,
                   borderBottomWidth: 0.5, borderBottomColor: '#F0F0F0' },
  rowIcon:       { width: 36, height: 36, borderRadius: 9,
                   justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  rowBody:       { flex: 1 },
  rowTitle:      { fontSize: 14, fontWeight: '600', color: '#2C3E50' },
  rowSub:        { fontSize: 12, color: '#7F8C8D', marginTop: 1 },

  badge:         { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20 },
  badgeText:     { fontSize: 11, fontWeight: '700' },

  versionFooter: { textAlign: 'center', fontSize: 12,
                   color: '#B0BEC5', marginTop: 24, marginBottom: 8 },
});